import type { SiteSnapshot } from "./api";

export function formatNumber(n: number): string {
  if (n >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return n.toLocaleString("en-US");
}

export function formatDelta(pct: number | null): string {
  if (pct === null) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

export function deltaClass(pct: number | null): string {
  if (pct === null || Math.abs(pct) < 0.05) return "text-muted";
  return pct > 0 ? "text-moss" : "text-rust";
}

/**
 * Compute today's value, day-over-day percent, and week-over-week percent
 * from a chronologically-ordered snapshot array.
 */
export interface MetricSummary {
  today: number;
  dod: number | null; // % change vs yesterday
  wow: number | null; // % change vs 7 days ago
}

export function summarize(
  snapshots: SiteSnapshot[],
  field: "visits" | "uniqueVisitors" | "pageviews",
): MetricSummary {
  if (snapshots.length === 0) return { today: 0, dod: null, wow: null };

  const last = snapshots[snapshots.length - 1];
  const today = last[field];

  const yesterday = snapshots.length >= 2 ? snapshots[snapshots.length - 2][field] : null;
  const weekAgo  = snapshots.length >= 8 ? snapshots[snapshots.length - 8][field] : null;

  const dod = yesterday !== null ? pctChange(yesterday, today) : null;
  const wow = weekAgo  !== null ? pctChange(weekAgo,  today)  : null;

  return { today, dod, wow };
}

function pctChange(prev: number, curr: number): number | null {
  if (prev === 0) return curr === 0 ? 0 : null;
  return ((curr - prev) / prev) * 100;
}

export function formatTime(epochMs: number): string {
  const d = new Date(epochMs);
  return d.toLocaleString("ja-JP", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Tokyo",
  });
}
